import { UnavailableBox } from '@common/components';
import { FormattedLink } from '@common/components/FormattedLink';
import { TextArea } from '@common/components/FormElements/Textarea';
import { NotesFieldWrapper, TitleText } from '@common/components/NotesField/NotesField.styles';
import { usePageContext } from '@common/components/PageContext';
import { Spinner } from '@common/components/Spinner';
import { useIsWindowWiderThan } from '@common/hooks';
import { analytics } from '@features/analytics';
import { useLabels } from '@features/Menu/useLabels';
import {
  useGetSpaPackage,
  useGetSpaPackageConfigGroups,
  useGetSpaPackageTimeslots,
  useGetSpaSettings,
} from '@features/spa';
import { useTranslations } from '@lang';
import { gridTheme } from '@utils/grid';
import moment from 'moment';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, Path, useFormContext, useWatch } from 'react-hook-form';
import { Property } from 'shared-types/Property';
import { SpaPackageType, TimeSlotType } from 'shared-types/SpaPackage';
import { entityHasAvailability } from '../../configGroupBookability';
import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import { DateTimeSelector } from './DateTimeSelector/DateTimeSelector';
import { GuestCounter } from './GuestCounter';
import { GuestDetailsAndSelections } from './GuestDetailsAndSelections/GuestDetailsAndSelections';
import { useQuantity } from './hooks';
import { MessageBoxWrapper, SpaPackageAvailabilityWrapper } from './SpaPackageAvailability.styles';

type SpaPackageAvailabilityProps = {
  spaPackageId: number;
  property: Property;
  unavailable: boolean;
  setUnavailable: (state: boolean) => void;
  language: string;
  maxGuests?: number;
};

export const SpaPackageAvailability: FC<SpaPackageAvailabilityProps> = ({
  spaPackageId,
  property,
  setUnavailable,
  unavailable,
  language,
  maxGuests,
}) => {
  const { parentPageName, routingData } = usePageContext();
  const { control, setValue } = useFormContext<SpaPackageFormValues>();

  const watchedDate = useWatch({ control, name: 'date' });
  const watchedGuests = useWatch({ control, name: 'guests' });
  const watchedArrivalTime = useWatch({ control, name: 'arrivalTime' });

  const selectedDateString = (watchedDate || moment().format('YYYY-MM-DD')).trim();

  const [startDate, setStartDate] = useState<string>(selectedDateString);

  const { data: spaPackage, isFetching: isFetchingSpaPackage } = useGetSpaPackage(
    property.id,
    spaPackageId,
    language,
    undefined,
    undefined,
    !unavailable
  );

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaPackageTimeslots(
    property.id,
    spaPackageId,
    startDate,
    spaPackage?.numberOfGuests,
    7,
    !unavailable
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const spaPhoneNumber = spaSettings?.phoneNumber || '';
  const showGenderNeutral = spaSettings?.showGenderNeutral || false;
  const { hideArrivalTimes } = spaSettings ?? { hideArrivalTimes: false };

  const { t } = useTranslations();
  const labels = useLabels();
  const { supportsTimeslots = false, name = '', id = 0, telephoneOnly = false } = spaPackage || {};

  const timeslotOptions = timeslots?.[selectedDateString];

  const [quantity, setQuantity] = useQuantity(
    Math.min(watchedGuests?.length || spaPackage?.numberOfGuests || 1, maxGuests || Infinity),
    () => {}
  );

  const selectedTime = watchedArrivalTime?.startTime;
  const endTime = timeslotOptions?.find(
    (slot: TimeSlotType) => slot.startTime === selectedTime
  )?.endTime;

  const isDesktop = useIsWindowWiderThan(gridTheme.breakpoints.md);

  const {
    data: configurationGroups,
    isFetching: isFetchingConfigGroups,
    error,
  } = useGetSpaPackageConfigGroups(
    spaPackage?.configurationGroups || [],
    property.id,
    id,
    selectedDateString,
    quantity,
    selectedTime,
    endTime
  );

  const loadingDates = (): string[] => {
    if (isFetchingSpaPackage || isFetchingTimeslots) {
      const days: string[] = [];
      for (let index = 0; index < 7; index++) {
        days.push(moment(startDate).add(index, 'days').format('YYYY-MM-DD'));
      }
      return days;
    } else if (isFetchingConfigGroups) {
      return [selectedDateString];
    } else return [];
  };

  const spaPackageWithConfig = {
    ...spaPackage,
    timeslots,
    configurationGroups:
      configurationGroups.length > 0 ? configurationGroups : spaPackage?.configurationGroups ?? [],
  };

  const anyDateHasTimeslots = timeslots
    ? Object.values(timeslots).some((slots: TimeSlotType[]) => slots?.length > 0)
    : false;

  const hasConfigGroupData = !!selectedTime && configurationGroups.length > 0;

  const noAvailableTimeslots =
    (supportsTimeslots && !anyDateHasTimeslots) ||
    (hasConfigGroupData && !entityHasAvailability(spaPackageWithConfig as SpaPackageType));

  const clearTimeSlots = () => {
    for (let i = 0; i < quantity; i++) {
      setValue(
        `guests.${i}.configurationGroups` as Path<SpaPackageFormValues>,
        undefined as never,
        {
          shouldValidate: true,
        }
      );
    }
  };

  const isLoading = isFetchingSpaPackage || isFetchingTimeslots || isFetchingConfigGroups;
  const hasFired = useRef(false);

  if (!isLoading && !loadingDates().length && unavailable && !telephoneOnly && !hasFired.current) {
    analytics.track({
      event: 'denial',
      params: {
        start_date: startDate,
        end_date: '',
        items: [
          {
            item_category: 'spa-packages',
            item_id: `${spaPackage?.id}`,
            item_name: spaPackage?.name,
          },
        ],
      },
    });
    hasFired.current = true;
  }

  useEffect(() => {
    if (isLoading) {
      hasFired.current = false;
      return;
    }
    if (!unavailable && (error || noAvailableTimeslots)) {
      setUnavailable(true);
    }
    if (unavailable && !error && !noAvailableTimeslots) {
      setUnavailable(false);
    }
  }, [isLoading, unavailable, error, noAvailableTimeslots, setUnavailable]);

  return (
    <SpaPackageAvailabilityWrapper>
      <Spinner showDelayMessage isActive={isLoading} />
      <GuestCounter
        quantity={quantity}
        setQuantity={setQuantity}
        incrementBy={spaPackageWithConfig?.numberOfGuests || 1}
        maxGuests={maxGuests}
        minGuests={spaPackageWithConfig?.numberOfGuests || 1}
      />
      <DateTimeSelector
        entity={spaPackageWithConfig as SpaPackageType}
        selectedTime={{ label: selectedTime ?? '', value: selectedTime ?? '' }}
        selectedDate={selectedDateString}
        onStartDateChange={setStartDate}
        loadingDates={loadingDates()}
        clearTimeSlots={clearTimeSlots}
        isLoading={isLoading}
        hideArrivalTimes={!!hideArrivalTimes}
      />
      {!telephoneOnly && (
        <GuestDetailsAndSelections
          quantity={quantity}
          display={!unavailable}
          entity={spaPackageWithConfig as SpaPackageType}
          showGenderNeutral={showGenderNeutral}
        />
      )}
      {!unavailable && !telephoneOnly && (
        <Controller
          name="note"
          control={control}
          render={({ field: { onChange, value } }) => (
            <NotesFieldWrapper>
              <TitleText>
                <TextArea
                  isFullWidth
                  name="note"
                  label={t('additional.notes')}
                  id="guest_note"
                  placeholder={t('booking.additionnalNotesPlaceholder')}
                  onChange={onChange}
                  input={{ value: value || '' }}
                  error=""
                />
              </TitleText>
            </NotesFieldWrapper>
          )}
        />
      )}
      {!isLoading && unavailable && !telephoneOnly && (
        <MessageBoxWrapper>
          <UnavailableBox
            title={t('spa.package.unavailableHeading', {
              spaDayCustomLabel: labels?.spadays || 'Spa Day',
            })}
            body={t('spa.package.unavailableDescription', {
              title: name,
              date: moment(selectedDateString).format('dddd Do MMMM YYYY'),
            })}
            footer={
              routingData && (
                <FormattedLink format="button" isPrimary={true} href={routingData.href}>
                  {routingData.message}
                </FormattedLink>
              )
            }
          />
        </MessageBoxWrapper>
      )}
      {telephoneOnly && (
        <MessageBoxWrapper>
          <UnavailableBox
            title={t('spa.telephoneOnly', { spaEntity: parentPageName })}
            body={t('spa.callUsOn', {
              phoneNumber: isDesktop ? (
                spaPhoneNumber
              ) : (
                <a href={`tel: ${spaPhoneNumber}`}>{spaPhoneNumber}</a>
              ),
            })}
          />
        </MessageBoxWrapper>
      )}
    </SpaPackageAvailabilityWrapper>
  );
};
